<template>
  <v-card outlined class="audit-panel pa-4">
    <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
      Search Audit Log
    </h3>

    <v-data-table
      :headers="headers"
      :items="audits"
      :items-per-page="pageSize"
      :server-items-length="totalEntries"
      :page.sync="currentPage"
      :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
      dense
      class="audit-table"
      no-data-text="No audit records available"
      show-expand
      :expanded.sync="expandedRows"
      item-key="id"
      @update:page="onPageChange"
      @update:items-per-page="onPageSizeChange"
    >
      <template v-slot:item.timestamp="{ item }">
        <span class="text-caption">{{ formatTime(item.timestamp) }}</span>
      </template>

      <template v-slot:item.searchType="{ item }">
        <v-chip x-small :color="typeColor(item.searchType)" text-color="white">
          {{ item.searchType }}
        </v-chip>
      </template>

      <template v-slot:item.queryText="{ item }">
        <span class="text-body-2 query-text">{{ item.queryText }}</span>
      </template>

      <template v-slot:item.resultCount="{ item }">
        <span class="text-body-2 font-weight-bold">{{ item.resultCount }}</span>
      </template>

      <template v-slot:item.executionTimeMs="{ item }">
        <v-chip x-small :color="speedColor(item.executionTimeMs)" text-color="white">
          {{ item.executionTimeMs }}ms
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center" v-if="isSuperAdmin">
          <v-btn icon x-small @click.stop="downloadAudit(item, 'full')" title="Download Full Audit (SuperAdmin)">
            <v-icon small color="#F59E0B">mdi-download</v-icon>
          </v-btn>
          <v-btn icon x-small @click.stop="downloadAudit(item, 'summary')" title="Download Summary Only">
            <v-icon small color="#007AFF">mdi-download-outline</v-icon>
          </v-btn>
        </div>
        <v-btn v-else icon x-small @click.stop="downloadAudit(item, 'summary')" title="Download Audit Summary">
          <v-icon small color="#007AFF">mdi-download</v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ headers: tableHeaders, item }">
        <td :colspan="tableHeaders.length" class="pa-4 expanded-detail">
          <div v-if="loadingDetailId === item.id" class="text-center py-3">
            <v-progress-circular indeterminate size="20" width="2" color="#007AFF" />
            <span class="text-caption ml-2 grey--text">Loading audit detail...</span>
          </div>
          <div v-else-if="auditDetails[item.id]">
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="detail-section mb-3">
                  <div class="text-caption font-weight-bold mb-1 theme-heading">Query Interpretation</div>
                  <div class="text-caption" v-if="auditDetails[item.id].interpretation">
                    {{ auditDetails[item.id].interpretation }}
                  </div>
                  <div class="text-caption grey--text" v-else>No interpretation data</div>
                </div>
                <div class="detail-section mb-3" v-if="auditDetails[item.id].extractedFacets && auditDetails[item.id].extractedFacets.length > 0">
                  <div class="text-caption font-weight-bold mb-1 theme-heading">Extracted Facets</div>
                  <v-chip v-for="(f, idx) in auditDetails[item.id].extractedFacets" :key="idx" x-small outlined color="primary" class="mr-1 mb-1">
                    {{ f.facetName || f.name || f }}
                  </v-chip>
                </div>
                <div class="detail-section mb-3" v-if="auditDetails[item.id].activeScoringWeights">
                  <div class="text-caption font-weight-bold mb-1 theme-heading">Active Scoring Weights</div>
                  <div class="text-caption" v-for="(val, key) in auditDetails[item.id].activeScoringWeights" :key="key">
                    <span class="grey--text">{{ key }}:</span> <span class="font-weight-bold">{{ val }}</span>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="detail-section mb-3" v-if="auditDetails[item.id].resultSet && auditDetails[item.id].resultSet.length > 0">
                  <div class="text-caption font-weight-bold mb-1 theme-heading">Top Results</div>
                  <div v-for="(r, ri) in auditDetails[item.id].resultSet.slice(0, 5)" :key="ri" class="d-flex align-center mb-1">
                    <span class="text-caption mr-2" style="min-width: 18px; color: #9E9E9E">#{{ ri + 1 }}</span>
                    <span class="text-caption flex-grow-1">{{ r.name || r.profileId }}</span>
                    <v-chip x-small :color="scoreColor(r.score)" text-color="white" class="ml-1">{{ r.score }}%</v-chip>
                  </div>
                </div>
                <div class="detail-section mb-3" v-if="isSuperAdmin && auditDetails[item.id].scoringDetail">
                  <div class="text-caption font-weight-bold mb-1" style="color: #F59E0B">
                    <v-icon x-small color="#F59E0B" class="mr-1">mdi-shield-lock</v-icon>
                    Full Scoring Breakdown (SuperAdmin)
                  </div>
                  <pre class="scoring-detail-pre">{{ formatScoringDetail(auditDetails[item.id].scoringDetail) }}</pre>
                </div>
                <div class="detail-section" v-else-if="!isSuperAdmin && auditDetails[item.id].scoringHighLevel">
                  <div class="text-caption font-weight-bold mb-1 theme-heading">Scoring Summary</div>
                  <div class="text-caption">{{ auditDetails[item.id].scoringHighLevel }}</div>
                </div>
              </v-col>
            </v-row>
          </div>
          <div v-else class="text-center py-3 text-caption grey--text">
            <v-icon small class="mr-1" color="#9E9E9E">mdi-information-outline</v-icon>
            Click the row or download button to load audit details
          </div>
        </td>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import * as analyticsApi from "../../api/IntelligenceAnalyticsApi";

export default {
  name: "AuditPanel",
  props: {
    audits: {
      type: Array,
      default: () => [],
    },
    page: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 20,
    },
    totalEntries: {
      type: Number,
      default: 0,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    isSuperAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPage: this.page,
      expandedRows: [],
      auditDetails: {},
      loadingDetailId: null,
    };
  },
  computed: {
    headers() {
      return [
        { text: "", value: "data-table-expand", width: "40px" },
        { text: "Time", value: "timestamp", width: "140px" },
        { text: "Type", value: "searchType", width: "110px" },
        { text: "Query", value: "queryText" },
        { text: "Results", value: "resultCount", width: "70px", align: "center" },
        { text: "Speed", value: "executionTimeMs", width: "90px", align: "center" },
        { text: "", value: "actions", width: this.isSuperAdmin ? "80px" : "40px", sortable: false },
      ];
    },
  },
  watch: {
    page(val) {
      this.currentPage = val;
    },
    expandedRows(val) {
      if (val && val.length > 0) {
        const item = val[val.length - 1];
        if (item && item.id && !this.auditDetails[item.id]) {
          this.loadDetail(item);
        }
      }
    },
  },
  methods: {
    onPageChange(page) {
      this.$emit("page-change", page);
    },
    onPageSizeChange(size) {
      this.$emit("page-size-change", size);
    },
    formatTime(timestamp) {
      if (!timestamp) return "—";
      const d = new Date(timestamp);
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    typeColor(type) {
      const colors = {
        keyword: "#007AFF",
        semantic: "#8B5CF6",
        smart_search: "#007AFF",
        jd_match: "#10B981",
        team_build: "#F59E0B",
      };
      return colors[type] || "#9E9E9E";
    },
    speedColor(ms) {
      if (ms <= 200) return "#10B981";
      if (ms <= 500) return "#F59E0B";
      return "#EF4444";
    },
    scoreColor(score) {
      if (score >= 70) return "#10B981";
      if (score >= 50) return "#F59E0B";
      return "#9E9E9E";
    },
    async loadDetail(item) {
      if (!item || !item.id) return;
      if (this.auditDetails[item.id]) return;
      this.loadingDetailId = item.id;
      try {
        const resp = await analyticsApi.GetSearchAuditDetail(item.id);
        if (resp && resp.success && resp.data) {
          this.$set(this.auditDetails, item.id, resp.data);
        } else {
          this.$set(this.auditDetails, item.id, this.buildFallbackDetail(item));
        }
      } catch {
        this.$set(this.auditDetails, item.id, this.buildFallbackDetail(item));
      }
      this.loadingDetailId = null;
    },
    buildFallbackDetail(item) {
      return {
        interpretation: item.queryText || "",
        extractedFacets: item.extractedFacets || [],
        activeScoringWeights: item.activeScoringWeights || null,
        resultSet: item.resultSet || [],
        scoringDetail: item.scoringDetail || null,
        scoringHighLevel: item.scoringHighLevel || item.categoryExplanation || "Scoring summary not available for this audit entry.",
      };
    },
    formatScoringDetail(detail) {
      if (typeof detail === "string") {
        try { return JSON.stringify(JSON.parse(detail), null, 2); } catch { return detail; }
      }
      return JSON.stringify(detail, null, 2);
    },
    async downloadAudit(item, level) {
      try {
        const resp = await analyticsApi.DownloadSearchAuditFull(item.id, level);

        if (resp && resp.success && resp.data) {
          const exportData = resp.data;
          const fileLabel = level === "full" && this.isSuperAdmin ? "full" : "summary";

          const blob = new Blob(
            [JSON.stringify(exportData, null, 2)],
            { type: "application/json" }
          );
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `audit_${item.id || "entry"}_${fileLabel}.json`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error("Audit download failed:", err);
      }
    },
  },
};
</script>

<style scoped>
.audit-table {
  border: 1px solid rgba(128,128,128,0.2);
  border-radius: 6px;
}
.query-text {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}
.expanded-detail {
  background: rgba(0, 122, 255, 0.03);
  border-left: 3px solid #007AFF;
}
.theme--dark .expanded-detail {
  background: rgba(0, 122, 255, 0.06);
}
.detail-section {
  padding: 8px;
  border-radius: 6px;
  background: rgba(128,128,128,0.04);
}
.theme--dark .detail-section {
  background: rgba(255,255,255,0.04);
}
.scoring-detail-pre {
  font-size: 11px;
  max-height: 200px;
  overflow-y: auto;
  background: rgba(0,0,0,0.05);
  border-radius: 4px;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: monospace;
}
.theme--dark .scoring-detail-pre {
  background: rgba(0,0,0,0.3);
  color: #ccc;
}
</style>
